import { EV } from '../core/eventbus.js';
import { createNet } from '../net/net.js';
import { PEER_COLORS } from '../systems/coop.js';

/**
 * 串门面板：注册 / 登录账号，选房间、连接，看谁在屋里，说两句话。
 * 模式：
 *   local  —— 同一台机器的多个标签页互访（零配置，开两个窗口即可）
 *   remote —— 跨设备，走公共 MQTT 中继，需要能连外网
 *
 * 认人靠 uid 不靠名字：注册后 uid 由服务器发放，换设备还是你；
 * 没登录就用本机身份（localStorage 里的号），换台机器就是另一个人。
 */
export class NetPanel {
  constructor(engine, netProxy, { coop, account, walkie } = {}) {
    this.engine = engine;
    this.net = netProxy;
    this.coop = coop;
    this.account = account || null;
    this.walkie = walkie || null;
    this.voiceBtn = document.getElementById('net-voice');
    this.voiceHintEl = document.getElementById('net-voice-hint');
    this.el = document.getElementById('net');
    this.roomInput = document.getElementById('net-room');
    this.nameInput = document.getElementById('net-name');
    this.passInput = document.getElementById('net-pass');
    this.meEl = document.getElementById('net-me');
    this.countEl = document.getElementById('net-count');
    this.adminBox = document.getElementById('net-admin');
    this.adminTokenInput = document.getElementById('net-admin-token');
    this.adminNameInput = document.getElementById('net-admin-name');
    this.adminOutEl = document.getElementById('net-admin-out');
    this.modeSel = document.getElementById('net-mode');
    this.statusEl = document.getElementById('net-status');
    this.listEl = document.getElementById('net-peers');
    this.sayInput = document.getElementById('net-say');

    if (!this.el) return;
    this.restore();
    this.bind();
  }

  restore() {
    const saved = localStorage.getItem('dtj_net');
    const p = saved ? JSON.parse(saved) : {};
    const params = new URLSearchParams(location.search);
    if (this.roomInput) this.roomInput.value = params.get('room') || p.room || this.randomRoom();
    if (this.nameInput) this.nameInput.value = (this.account && this.account.name) || p.name || '居者';
    if (this.modeSel) this.modeSel.value = params.get('mode') || p.mode || 'local';
    this.refreshIdentity();
    this.renderPeers(this.coop ? this.coop.peerList() : []);
    // 管理区平时藏起来，网址带 ?admin=1 才露出来（屋主自己用的，免得别人误点）
    if (this.adminBox && new URLSearchParams(location.search).get('admin') === '1') {
      this.adminBox.classList.remove('hidden');
    }
  }

  randomRoom() { return String(Math.floor(1000 + Math.random() * 9000)); }

  bind() {
    const joinBtn = document.getElementById('net-join');
    const leaveBtn = document.getElementById('net-leave');
    const signupBtn = document.getElementById('net-signup');
    const loginBtn = document.getElementById('net-login');
    const logoutBtn = document.getElementById('net-logout');
    const killBtn = document.getElementById('net-kill');
    const adminListBtn = document.getElementById('net-admin-list');
    const adminDelBtn = document.getElementById('net-admin-del');
    const closeBtn = this.el.querySelector('[data-close]');
    const btnNet = document.getElementById('btn-net');

    if (btnNet) btnNet.onclick = () => this.toggle();
    if (joinBtn) joinBtn.onclick = () => this.join();
    if (leaveBtn) leaveBtn.onclick = () => this.leave();
    if (signupBtn) signupBtn.onclick = () => this.doAccount('signup');
    if (loginBtn) loginBtn.onclick = () => this.doAccount('login');
    if (logoutBtn) logoutBtn.onclick = () => this.doAccount('logout');
    if (killBtn) killBtn.onclick = () => this.killAccount();
    if (adminListBtn) adminListBtn.onclick = () => this.adminDo('list');
    if (adminDelBtn) adminDelBtn.onclick = () => this.adminDo('delete');
    if (closeBtn) closeBtn.onclick = () => this.toggle(false);
    if (this.sayInput) {
      this.sayInput.onkeydown = (e) => {
        if (e.key !== 'Enter') return;
        this.coop && this.coop.say(this.sayInput.value);
        this.sayInput.value = '';
      };
    }

    // 改了称呼就地广播，屋里人的名牌跟着变（登录状态下称呼就是账号名，不许改）
    if (this.nameInput) {
      this.nameInput.onchange = () => {
        if (this.account && this.account.verified) {
          this.nameInput.value = this.account.name;
          return;
        }
        const name = (this.nameInput.value.trim() || '居者').slice(0, 6);
        this.engine.me = this.engine.me || {};
        this.engine.me.name = name;
        localStorage.setItem('dtj_net', JSON.stringify({
          ...this.savedPrefs(), name,
        }));
        if (!this.net.open) return;
        this.sendHello();
      };
    }

    // 对讲机：面板里这个按钮按住说话，松手就发出去；与键盘 V、触屏「讲」键是同一件事
    if (this.voiceBtn && this.walkie) {
      const down = (e) => { this.walkie.start(); e.preventDefault(); };
      const up = (e) => { this.walkie.stop(); if (e) e.preventDefault(); };
      this.voiceBtn.addEventListener('pointerdown', down, { passive: false });
      this.voiceBtn.addEventListener('pointerup', up, { passive: false });
      this.voiceBtn.addEventListener('pointerleave', up, { passive: false });
      this.voiceBtn.addEventListener('pointercancel', up, { passive: false });
      this.voiceBtn.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    this.engine.events.on(EV.VOICE_STATE, (v) => this.renderVoice(v.state));
    this.renderVoice('idle');
    this.engine.events.on(EV.NET_PEERS, (list) => this.renderPeers(list));
  }

  /** 录音状态：按钮亮起来，提示跟着变 */
  renderVoice(state) {
    if (this.voiceBtn) this.voiceBtn.classList.toggle('on', state === 'rec');
    if (!this.voiceHintEl) return;
    if (!this.walkie || !this.walkie.constructor.supported()) {
      this.voiceHintEl.textContent = '这个浏览器不让录音，换 Chrome / Edge 再试';
      return;
    }
    if (state === 'rec') this.voiceHintEl.textContent = '松开就发出去';
    else if (state === 'send') this.voiceHintEl.textContent = '正在发出去…';
    else this.voiceHintEl.textContent = '按住说话，松手发一条（键盘按 V 也一样）';
  }

  savedPrefs() {
    try { return JSON.parse(localStorage.getItem('dtj_net') || '{}'); } catch { return {}; }
  }

  /** 当前身份一行字：登录了显示账号，没登录提示是本机身份 */
  refreshIdentity() {
    const a = this.account;
    if (!this.meEl) return;
    if (a && a.verified) {
      this.meEl.textContent = `${a.name}　已注册（${a.uid.slice(0, 10)}）`;
      if (this.nameInput) { this.nameInput.value = a.name; this.nameInput.disabled = true; }
    } else {
      const short = a ? a.uid.slice(0, 10) : '';
      this.meEl.textContent = short ? `未登录，本机身份 ${short}，换设备会变` : '未登录，用本机身份';
      if (this.nameInput) this.nameInput.disabled = false;
    }
  }

  async doAccount(action) {
    if (!this.account) { this.status('账号服务没接上（本机预览没有后端）', 'bad'); return; }

    if (action === 'logout') {
      this.account.logout();
      this.refreshIdentity();
      this.status('已退出，改回本机身份', '');
      if (this.net.open) this.sendHello();   // 让屋里看到我的新身份
      return;
    }

    const name = (this.nameInput && this.nameInput.value.trim()) || '';
    const pass = (this.passInput && this.passInput.value) || '';
    if (pass.length < 4) { this.status('口令至少 4 位', 'bad'); return; }

    this.status(action === 'signup' ? '正在注册…' : '正在登录…', '');
    try {
      const r = action === 'signup'
        ? await this.account.signup(name, pass)
        : await this.account.login(name, pass);
      this.engine.me = this.engine.me || {};
      this.engine.me.name = r.name;
      localStorage.setItem('dtj_net', JSON.stringify({ ...this.savedPrefs(), name: r.name }));
      if (this.passInput) this.passInput.value = '';
      this.refreshIdentity();
      this.blurInputs();   // 收起手机软键盘，也免得之后按 WASD 打进口令框
      this.status(`${r.name}（${r.uid.slice(0, 10)}）`, 'good');
      if (this.net.open) this.sendHello();
    } catch (err) {
      this.status(err && err.message ? err.message : '账号服务没回应', 'bad');
    }
  }

  /** 注销自己的号：服务器上的记录删掉，本机退回未登录（书架的书不动） */
  async killAccount() {
    const a = this.account;
    if (!a) { this.status('账号服务没接上（本机预览没有后端）', 'bad'); return; }
    if (!a.verified) { this.status('还没登录，没什么可注销的', 'bad'); return; }

    const name = a.name;
    const ok = confirm(`注销「${name}」？\n服务器上的号会删掉，名字可以让别人再用；书架上的书不动。`);
    if (!ok) return;

    const pass = (this.passInput && this.passInput.value) || '';
    this.status('正在注销…', '');
    try {
      await a.remove(pass);
      if (this.passInput) this.passInput.value = '';
      this.refreshIdentity();
      this.blurInputs();
      this.status(`已注销「${name}」，改回本机身份`, '');
      this.engine.events.emit(EV.TOAST, { text: `已注销「${name}」`, kind: 'info' });
      if (this.net.open) this.sendHello();   // 屋里人看到我变回本机身份
    } catch (err) {
      this.status(err && err.message ? err.message : '注销失败', 'bad');
    }
  }

  /** 管理员：列名单 / 按名字清号，都凭书库口令 */
  async adminDo(what) {
    const token = (this.adminTokenInput && this.adminTokenInput.value.trim()) || '';
    const name = (this.adminNameInput && this.adminNameInput.value.trim()) || '';
    if (!this.account) { this.adminOut('账号服务没接上（本机预览没有后端）'); return; }
    if (!token) { this.adminOut('先填书库口令。'); return; }

    if (what === 'list') {
      this.adminOut('正在取名册…');
      try {
        const r = await this.account.adminList(token);
        const lines = (r.accounts || []).map((x) => {
          const d = new Date(x.lastSeen || 0);
          return `${x.name}　${x.uid}　${isNaN(d.getTime()) ? '-' : d.toLocaleDateString()}`;
        });
        const tail = r.shown < r.total ? `（只列最近 ${r.shown} 个）` : '';
        this.adminOut(`共 ${r.total} 个号${tail}\n` + (lines.join('\n') || '（还没有人注册）'));
      } catch (err) {
        this.adminOut(err && err.message ? err.message : '取名册失败');
      }
      return;
    }

    if (!name) { this.adminOut('填一下要清的名字。'); return; }
    if (!confirm(`以管理员身份注销「${name}」？`)) return;
    this.adminOut('正在清号…');
    try {
      const r = await this.account.adminRemove(name, token);
      this.adminOut(`已清掉「${r.name}」（${r.uid}）`);
      this.status(`已清掉「${r.name}」`, '');
      this.renderPeers(this.coop ? this.coop.peerList() : []);
    } catch (err) {
      this.adminOut(err && err.message ? err.message : '清号失败');
    }
  }

  adminOut(text) { if (this.adminOutEl) this.adminOutEl.textContent = text; }

  /** 让输入框交出焦点：手机上软键盘随之收起，键盘不再压着摇杆 */
  blurInputs() {
    const a = document.activeElement;
    if (a && a !== document.body && typeof a.blur === 'function') a.blur();
  }

  toggle(force) {
    const show = force === undefined ? this.el.classList.contains('hidden') : force;
    this.el.classList.toggle('hidden', !show);
  }

  status(text, kind = '') {
    if (this.statusEl) {
      this.statusEl.textContent = text;
      this.statusEl.className = kind;
    }
    this.engine.events.emit(EV.NET_STATUS, { text, kind });
  }

  sendHello() {
    const p = this.engine.scene && this.engine.scene.first('transform', 'player');
    this.net.send('hello', {
      name: (this.engine.me && this.engine.me.name) || '居者',
      color: (this.engine.me && this.engine.me.color) || PEER_COLORS[0],
      verified: this.net.verified,
      x: p ? p.transform.x : 0,
      y: p ? p.transform.y : 0,
      facing: p ? p.anim.facing : 'down',
    });
  }

  async join() {
    const room = (this.roomInput && this.roomInput.value.trim()) || 'public';
    const name = (this.account && this.account.name)
      || (this.nameInput && this.nameInput.value.trim())
      || '居者';
    const mode = this.modeSel ? this.modeSel.value : 'local';

    this.engine.me = this.engine.me || {};
    this.engine.me.name = name.slice(0, 6);
    this.engine.me.color = PEER_COLORS[Math.floor(Math.random() * PEER_COLORS.length)];

    localStorage.setItem('dtj_net', JSON.stringify({ ...this.savedPrefs(), room, name, mode }));
    const url = new URL(location.href);
    url.searchParams.set('room', room);
    history.replaceState(null, '', url);

    this.status('正在连接…');
    const net = createNet(mode, room);
    try {
      await net.connect();
    } catch (err) {
      this.status('连接失败：' + err.message, 'bad');
      return;
    }

    this.net.swap(net);
    this.sendHello();
    net.send('tv:ask', {});   // 问问屋里正在播什么

    const label = mode === 'remote' ? '已连上（跨设备）' : '已连上（本机多开）';
    this.status(`${label}　房间 ${room}`, 'good');
    this.engine.events.emit(EV.TOAST, { text: `${label}：${room}`, kind: 'good' });
    this.renderPeers(this.coop ? this.coop.peerList() : []);
  }

  leave() {
    if (this.coop) this.coop.leave();
    if (this.walkie) this.walkie.dispose();   // 麦克风交回去，排队的话也丢掉
    this.net.disconnect();
    this.status('已离开房间', '');
    this.renderPeers([]);
  }

  renderPeers(list) {
    const n = (list ? list.length : 0) + 1;   // 连自己在内
    if (this.countEl) {
      const lead = this.coop && this.net.open && this.coop.isLeader && this.coop.isLeader()
        ? '　你在带着屋里走时辰' : '';
      this.countEl.textContent = `屋里 ${n} 人${lead}`;
    }
    if (!this.listEl) return;
    if (!list || !list.length) {
      this.listEl.innerHTML = '<span class="muted">屋里只有你</span>';
      return;
    }
    this.listEl.innerHTML = list
      .map((p) => {
        const mark = p.verified ? '<b class="v" title="已注册账号">证</b>' : '';
        const tip = p.verified ? ' title="已注册账号"' : '';
        return `<span class="peer"${tip}><i style="background:${p.color}"></i>${p.name}${mark}</span>`;
      })
      .join('');
  }
}
